/* global Sys, Core, Services, Language, Utils */
Sys.ns("Core");

/**
 * The error manager controller.
 *
 * @class Core.ErrorManagerController
 * @extends Core.Controller
 */
Core.ErrorManagerController = {

    /**
     * @inheritdoc
     */
    constructor: function() {
        Core.ErrorManagerController.superclass.constructor.apply(this, arguments);
    },

    /**
     * @inheritdoc
     */
    setupEvents: function() {
        this.on({
            "request:errorManager.handleError": this.handleError.bind(this),
            "request:errorManager.handleRequestError": this.handleRequestError,

            // @deprecated
            "request:errorManager.handleGameInactive": this.handleGameInactive,
            "request:errorManager.handleClientOutOfMoneyError": this.handleClientOutOfMoneyError
        });
    },

    /**
     * Shows an error dialog.
     *
     * This is broken out to this function to allow for easy overriding.
     *
     * @private
     * @param {Object} config The configuration object for the error to display.
     * @returns {void}
     */
    showDialog: function(config) {
        var errorCode = config.errorCode === "http" ? 0 : config.errorCode;

        this.fireEvent("notify:errorManager.error", errorCode, Services.languageManager.getText(errorCode));

        Services.dialog.show(config);
    },

    /**
     * Shows an error dialog for the specified error code.
     *
     * @private
     * @param {number|string} errorCode The error code we want to show a dialog for.
     * @returns {void}
     */
    showDialogForErrorCode: function(errorCode) {
        this.showDialog(this.getDialogConfig(errorCode));
    },

    /**
     * Handles error.
     *
     * @private
     * @param {number|string} errorCode The error code to be handled.
     * @returns {void}
    */
    handleError: function(errorCode) {
        Utils.Helpers.logError("GCS_RUNTIME_ERROR", errorCode, new Error("Runtime error"));
        this.showDialogForErrorCode(errorCode);
    },

    /**
     * Request handler handling any errors encountered in Sys.utils.httpGet.
     *
     * @private
     * @param {XMLHttpRequest} serverRequest Request object as returned by Sys.utils.httpGet.
     * @returns {void}.
     */
    handleRequestError: function(serverRequest) {
        var config = this.handleHttpErrors(serverRequest),
            errorCode;

        if (!config) {
            config = this.handleIntegrationSpecificErrors(serverRequest);
        }

        if (!config) {
            config = this.handleGameSpecificErrors(serverRequest);
        }

        if (!config) {
            config = this.handleServerErrors(serverRequest);
        }

        if (!config) {
            this.fireEvent("notify:errorManager.noErrorDetected");
        }
        else {
            if (config.fatal) {
                this.fireEvent("request:audioPlayer.stopAll");
            }

            errorCode = config.errorCode === "http" ? 0 : config.errorCode;
            Utils.Helpers.logError("GCS_RUNTIME_ERROR", errorCode, new Error("Runtime server request error"));

            this.showDialog(config);
        }
    },

    /**
     * Detects any http errors of the request and provides any error dialog config.
     *
     * @private
     * @param {XMLHttpRequest} serverRequest Request object as returned by Sys.utils.httpGet.
     * @returns {Object|undefined} A config object to send with a "request:dialogWindow.show" event following the error or undefined if no http error was detected.
     */
    handleHttpErrors: function(serverRequest) {
        if (Sys.utils.httpRequestIsOK(serverRequest)) {
            return undefined;
        }

        return this.getDialogConfig("http");
    },

    /**
     * Stub function that can be overridden to handle integration specific errors before the standard server error handling is made.
     *
     * @private
     * @param {XMLHttpRequest} serverRequest Request object as returned by Sys.utils.httpGet.
     * @returns {Object|undefined} A config object to send with a "request:dialogWindow.show" event following the error or undefined if no error was detected.
     */
    handleIntegrationSpecificErrors: function(serverRequest) {
        // Stub
    },

    /**
     * Stub function that can be overridden to handle game specific errors before the standard server error handling is made.
     *
     * @private
     * @param {XMLHttpRequest} serverRequest Request object as returned by Sys.utils.httpGet.
     * @returns {Object|undefined} A config object to send with a "request:dialogWindow.show" event following the error or undefined if no error was detected.
     */
    handleGameSpecificErrors: function(serverRequest) {
        // Stub
    },

    /**
     * Detects any game server errors of the request and provides any error dialog config.
     *
     * @private
     * @param {XMLHttpRequest} serverRequest Request object as returned by Sys.utils.httpGet.
     * @returns {Object|undefined} A config object to send with a "request:dialogWindow.show" event following the error or undefined if no error was detected.
     */
    handleServerErrors: function(serverRequest) {
        var errorCode = Sys.utils.getErrorCode(serverRequest);

        if (!Sys.isDefined(errorCode)) {
            return undefined;
        }

        return this.getDialogConfig(errorCode);
    },

    /**
     * Skeleton method allowing for an Android override.
     *
     * NOTE: Has an Android override.
     *
     * @returns {void}
     */
    handleGameInactive: function() {
        // Stub
    },

    /**
     * Handler for when a client out of money error is triggered.
     *
     * @private
     * @returns {void}
     */
    handleClientOutOfMoneyError: function() {
        var config = this.model.setUpOutOfMoneyErrorConfig();

        config.buttons = this.getButtons(config.buttons);
        Utils.Helpers.logError("GCS_RUNTIME_ERROR", 402, new Error("Client out of money error"));
        this.showDialog(config);
    },

    /**
     * Gets the error dialog config for the specified error code.
     *
     * @private
     * @param {string|number} errorCode The error code for the error.
     * @returns {{title: (*|string), text: *, buttons: Object[], fatal: *, hideGame: boolean}} Dialog config object.
     */
    getDialogConfig: function(errorCode) {
        var baseConfig = this.model.getDialogConfig(errorCode);

        return {
            title: baseConfig.title,
            text: baseConfig.text,
            buttons: this.getButtons(baseConfig.buttons),
            fatal: Boolean(baseConfig.fatal),
            hideGame: Boolean(baseConfig.hideGame),
            errorCode: errorCode
        };
    },

    /**
     * Utility function to convert the button config from the model to an array of button configs usable for the
     * dialogWindow.
     *
     * @private
     * @param {Object[]} buttons Button config array.
     * @returns {Object[]} Array of buttons.
     */
    getButtons: function(buttons) {
        var me = this;

        return buttons.map(function(button) {
            return me.getButton(button);
        });
    },

    /**
     * Convert the button config from the model to a button configs usable for the dialogWindow.
     *
     * @private
     * @param {Object} buttonConfig Button config.
     * @param {string} buttonConfig.type The button type.
     * @param {string|number} [buttonConfig.reason] The reason (a decimal integer) given for going to lobby.
     * @returns {{text: string, action: Function}} Button object.
     */
    getButton: function(buttonConfig) {
        var me = this,
            buttonType = buttonConfig.type;

        switch (buttonType) {
            case "casino":
                return {
                    text: Services.languageManager.getText(Language.Keys.btn_casino),
                    primary: buttonConfig.primary ? buttonConfig.primary : false,
                    action: function() {
                        me.onButtonInteraction("home");
                        Sys.utils.goToLobby(buttonConfig.reason);
                    }
                };
            case "reload":
                return {
                    text: Services.languageManager.getText(Language.Keys.btn_reload),
                    primary: buttonConfig.primary ? buttonConfig.primary : false,
                    action: function() {
                        me.onButtonInteraction(buttonType);
                        Sys.utils.reload();
                    }
                };
            case "close":
                return {
                    text: Services.languageManager.getText(Language.Keys.btn_close),
                    primary: buttonConfig.primary ? buttonConfig.primary : false,
                    action: function() {
                        me.onButtonInteraction(buttonType);
                    }
                };

            case "closeBonusProgramWidget":
                return {
                    text: Services.languageManager.getText(Language.Keys.btn_close),
                    primary: buttonConfig.primary ? buttonConfig.primary : false,
                    action: function() {
                        me.onButtonInteraction(buttonType);
                        me.closeBonusProgramWidget();
                    }
                };

            case "closeAndRevertSpin":
                return {
                    text: Services.languageManager.getText(Language.Keys.btn_close),
                    primary: buttonConfig.primary ? buttonConfig.primary : false,
                    action: function() {
                        me.onButtonInteraction(buttonType);
                        me.revertSpin();
                    }
                };
            case "reduceBetAndRevertSpin":
                return {
                    text: Services.languageManager.getText(Language.Keys.btn_reduceBet),
                    primary: buttonConfig.primary ? buttonConfig.primary : false,
                    action: function() {
                        me.onButtonInteraction(buttonType);
                        me.revertSpin();
                        me.openBetSettings();
                    }
                };
            case "reduceBet":
                return {
                    text: Services.languageManager.getText(Language.Keys.btn_reduceBet),
                    primary: buttonConfig.primary ? buttonConfig.primary : false,
                    action: function() {
                        me.onButtonInteraction(buttonType);
                        me.openBetSettings();
                    }
                };
            case "deposit":
                return {
                    text: Services.languageManager.getText(Language.Keys.btn_deposit),
                    primary: buttonConfig.primary ? buttonConfig.primary : false,
                    action: function() {
                        me.onButtonInteraction(buttonType);
                        Services.moneyManager.reloadBalance();
                        Sys.utils.goToCashier();
                    }
                };
            default:
                return {
                    text: "[ERROR]",
                    action: function() {
                        console.error("this button was not correctly configured in the error manager");
                    }
                };
        }
    },

    /**
     * Requests that the bet settings should open.
     *
     * @private
     * @returns {void}
     */
    openBetSettings: function() {
        // Keypad Classic
        this.fireEvent("request:betSettings.show");

        // PlayUI
        this.fireEvent("request:bet.reduceBet");
    },

    /**
     * Requests that spin should be reverted.
     *
     * @private
     * @returns {void}
     */
    revertSpin: function() {
        this.fireEvent("request:moneyManager.revertBet");
        this.fireEvent("request:spin.activateDefaultOutcome", "basic");
    },

    /**
     * Notifies that the bonus program widget is to be closed.
     *
     * @private
     * @returns {void}
     */
    closeBonusProgramWidget: function() {
        this.fireEvent("notify:bonusProgramWidget.close");
    },

    /**
     * Notifies that the error dialog was closed.
     *
     * @private
     * @param {string} buttonType The button type.
     * @returns {void}
     */
    onButtonInteraction: function(buttonType) {
        this.fireEvent("notify:errorManager.errorDialogClosed");
        this.fireEvent("notify:dialogBoxClosed", { name: "error", button: buttonType });
    }
};

Core.ErrorManagerController = Sys.extend(Core.Controller, Core.ErrorManagerController, "Core.ErrorManagerController");
